import {
  KuxFilteredCategoriesDTO,
  UIDAStructureTypeEnum,
  UIDAPresentationTypeEnum,
  AssetPresentationRatioEnum,
  AssetPresentationFormEnum,
  PREDEFINED_REFERENCES,
} from '@kaltura-ott/tvpil';
import { action, observable } from 'mobx';

import { errorHandling } from '../../../utils/error-handling';
import { ErrorPriority } from '../../error';

export interface GridPage {
  id: number;
  referenceId: string;
  targetGridCategoryId?: number;
  collectionId?: number;
  categoryId: number;
  uiDesignElements: KuxFilteredCategoriesDTO['uiDesignElements'];
  anchors: string[];
  menuType?: 'main' | 'sub' | '';
  parentalPinProtected: boolean;
  presentFavoriteIndicator: boolean;
  presentEntitlementIndicator: boolean;
  assetPresentationForm?: AssetPresentationFormEnum;
  ratioForAllItems?: AssetPresentationRatioEnum;
  assetPresentationImageType?: number;
  imageHeight?: number;
  imageHeightCompact?: number;
  imageHeightMedium?: number;
  presentRating: boolean;
  ratingProvider?: string;
  image?: string;
  presentRatingSourceLogo: boolean;
  name: string;
}

export const GRID_DEFAULT_ANCHOR = 'default_grid';

/**
 * @category Store
 */
export class GridStore {
  static moduleName = 'GridStore';

  @observable isReady = false;

  type: UIDAStructureTypeEnum.aside = UIDAStructureTypeEnum.aside;
  subtype: UIDAPresentationTypeEnum.grid = UIDAPresentationTypeEnum.grid;

  private list: GridPage[];

  constructor(gridsTree: KuxFilteredCategoriesDTO[]) {
    this.list = gridsTree.map((tree) => ({
      id: tree.id,
      referenceId: tree.referenceId,
      anchors: tree?.anchors || [GRID_DEFAULT_ANCHOR],
      categoryId: tree?.id,
      uiDesignElements: tree.uiDesignElements,
      parentalPinProtected: tree?.parentalPinProtected,
      presentEntitlementIndicator: tree?.asideProperty?.presentEntitlementIndicator,
      presentFavoriteIndicator: tree?.asideProperty?.presentFavoriteIndicator,
      ratioForAllItems: tree?.railImageProperty?.assetPresentationRatio,
      assetPresentationImageType: tree?.railImageProperty.assetPresentationImageType,
      //https://kaltura.atlassian.net/wiki/spaces/KUXDEV/pages/3949036288/Support+Grid+screens+in+different+screen+sizes#UIDA-asset-update
      imageHeight: tree?.imageScreens.imageHeight,
      imageHeightCompact: tree?.imageScreens.imageHeightCompact,
      imageHeightMedium: tree?.imageScreens.imageHeightMedium,
      presentRating: tree?.asideProperty?.presentRating,
      ratingProvider: tree?.asideProperty?.ratingProvider,
      presentRatingSourceLogo: tree?.asideProperty?.presentRatingSourceLogo,
      name: tree?.name,
    }));
  }

  @action
  @errorHandling({ type: ErrorPriority.high })
  init() {
    this.isReady = true;
  }

  checkGridAvailability(referenceId: string) {
    return !!this.list.find((details) => details.referenceId === referenceId);
  }

  getDefault() {
    return this.list.find((details) => details.referenceId === PREDEFINED_REFERENCES.grid);
  }

  getById(id: number) {
    return this.list.find((details) => details.id === id) || this.getDefault();
  }

  getByReferenceId(referenceId: string) {
    return this.list.find((details) => details.referenceId === referenceId) || this.getDefault();
  }
}
